"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { GuideCard } from "./guide-card";

export interface GuideDirectoryItem {
	slug: string;
	title: string;
	description: string;
	/** Raw content-type key: monograph | howto | comparison | basics. */
	category: string;
	categoryLabel: string;
	readingTime?: number;
	readingTimeLabel?: string;
	heroImage?: string | null;
	heroImageAlt?: string | null;
	/** Research-area slugs (shared taxonomy, curated). */
	researchAreas: string[];
	/** Substance slugs (curated via PEPTIDE_LINKS). */
	substances: string[];
	/** Position in the server's newest-first ordering (0 = newest). */
	recencyIndex: number;
}

const CONTENT_TYPES = ["monograph", "howto", "comparison", "basics"] as const;
type GuideSort = "newest" | "readingTime" | "title";

interface GuidesDirectoryProps {
	items: GuideDirectoryItem[];
	/** Research-area slug → localized label. */
	areaLabels: Record<string, string>;
	/** Substance slug → localized label (only substances that occur in items). */
	substanceLabels: Record<string, string>;
	locale: string;
	channel: string;
}

/**
 * Interactive guide directory. The FULL guide list (~37 entries) is
 * server-rendered; search/filters/sort only reorder or hide entries client-
 * side — the canonical URL stays clean, so filters can never create
 * indexable URL permutations.
 */
export function GuidesDirectory({
	items,
	areaLabels,
	substanceLabels,
	locale,
	channel,
}: GuidesDirectoryProps) {
	const t = useTranslations("guides");
	const [query, setQuery] = useState("");
	const [types, setTypes] = useState<ReadonlySet<string>>(new Set());
	const [areas, setAreas] = useState<ReadonlySet<string>>(new Set());
	const [substance, setSubstance] = useState<string>("all");
	const [sort, setSort] = useState<GuideSort>("newest");
	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

	const availableAreas = useMemo(() => {
		const present = new Set<string>();
		for (const item of items) for (const area of item.researchAreas) present.add(area);
		return Object.keys(areaLabels).filter((slug) => present.has(slug));
	}, [items, areaLabels]);

	const substanceOptions = useMemo(
		() =>
			Object.entries(substanceLabels)
				.filter(([slug]) => items.some((i) => i.substances.includes(slug)))
				.sort(([, a], [, b]) => a.localeCompare(b, locale)),
		[items, substanceLabels, locale],
	);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		const result = items.filter((item) => {
			if (q && !item.title.toLowerCase().includes(q) && !item.description.toLowerCase().includes(q)) {
				return false;
			}
			if (types.size > 0 && !types.has(item.category)) return false;
			if (areas.size > 0 && !item.researchAreas.some((a) => areas.has(a))) return false;
			if (substance !== "all" && !item.substances.includes(substance)) return false;
			return true;
		});
		switch (sort) {
			case "readingTime":
				return [...result].sort(
					(a, b) => (a.readingTime ?? Number.MAX_SAFE_INTEGER) - (b.readingTime ?? Number.MAX_SAFE_INTEGER),
				);
			case "title":
				return [...result].sort((a, b) => a.title.localeCompare(b.title, locale));
			default:
				return [...result].sort((a, b) => a.recencyIndex - b.recencyIndex);
		}
	}, [items, query, types, areas, substance, sort, locale]);

	const activeFilterCount = types.size + areas.size + (substance !== "all" ? 1 : 0);

	const toggle = (set: ReadonlySet<string>, value: string): Set<string> => {
		const next = new Set(set);
		if (next.has(value)) next.delete(value);
		else next.add(value);
		return next;
	};

	const clearAll = () => {
		setQuery("");
		setTypes(new Set());
		setAreas(new Set());
		setSubstance("all");
	};

	const chip = (selected: boolean) =>
		cn(
			"rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors",
			selected
				? "border-foreground bg-foreground text-background"
				: "hover:border-foreground/30 border-border bg-card text-muted-foreground hover:text-foreground",
		);

	const filterPanel = (
		<div className="space-y-4">
			<div>
				<p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
					{t("filterTypeLabel")}
				</p>
				<div className="flex flex-wrap gap-1.5">
					{CONTENT_TYPES.map((type) => (
						<button
							key={type}
							type="button"
							aria-pressed={types.has(type)}
							onClick={() => setTypes(toggle(types, type))}
							className={chip(types.has(type))}
						>
							{t(`category.${type}`)}
						</button>
					))}
				</div>
			</div>

			{availableAreas.length > 0 && (
				<div>
					<p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
						{t("filterAreaLabel")}
					</p>
					<div className="flex flex-wrap gap-1.5">
						{availableAreas.map((slug) => (
							<button
								key={slug}
								type="button"
								aria-pressed={areas.has(slug)}
								onClick={() => setAreas(toggle(areas, slug))}
								className={chip(areas.has(slug))}
							>
								{areaLabels[slug]}
							</button>
						))}
					</div>
				</div>
			)}

			{substanceOptions.length > 0 && (
				<div className="flex flex-wrap items-center gap-3">
					<label
						htmlFor="guide-substance"
						className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
					>
						{t("filterSubstanceLabel")}
					</label>
					<select
						id="guide-substance"
						value={substance}
						onChange={(e) => setSubstance(e.target.value)}
						className="h-9 rounded-lg border border-border bg-card px-2.5 text-sm shadow-xs focus:border-primary focus:outline-none"
					>
						<option value="all">{t("filterSubstanceAll")}</option>
						{substanceOptions.map(([slug, label]) => (
							<option key={slug} value={slug}>
								{label}
							</option>
						))}
					</select>
				</div>
			)}
		</div>
	);

	return (
		<div>
			{/* Controls */}
			<div className="flex flex-wrap items-center gap-2.5">
				<div className="relative min-w-0 flex-1 sm:max-w-xs">
					<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<input
						type="search"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder={t("searchPlaceholder")}
						aria-label={t("searchPlaceholder")}
						className="focus:ring-primary/10 h-10 w-full rounded-lg border border-border bg-card pl-9 pr-8 text-sm shadow-xs placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2"
					/>
					{query && (
						<button
							type="button"
							onClick={() => setQuery("")}
							aria-label={t("clearFilters")}
							className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground hover:text-foreground"
						>
							<X className="h-3.5 w-3.5" />
						</button>
					)}
				</div>

				<button
					type="button"
					onClick={() => setMobileFiltersOpen((v) => !v)}
					aria-expanded={mobileFiltersOpen}
					className="hover:bg-muted/50 inline-flex h-10 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm font-medium shadow-xs lg:hidden"
				>
					<SlidersHorizontal className="h-4 w-4" />
					{t("filtersLabel")}
					{activeFilterCount > 0 && (
						<span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-bold tabular-nums text-background">
							{activeFilterCount}
						</span>
					)}
				</button>

				<div className="ml-auto flex items-center gap-2.5">
					<span className="text-sm tabular-nums text-muted-foreground" aria-live="polite">
						{t("resultCountLabel", { count: filtered.length })}
					</span>
					<label htmlFor="guide-sort" className="sr-only">
						{t("sortLabel")}
					</label>
					<select
						id="guide-sort"
						value={sort}
						onChange={(e) => setSort(e.target.value as GuideSort)}
						className="h-10 rounded-lg border border-border bg-card px-2.5 text-sm shadow-xs focus:border-primary focus:outline-none"
					>
						<option value="newest">{t("sortNewest")}</option>
						<option value="readingTime">{t("sortReadingTime")}</option>
						<option value="title">{t("sortTitle")}</option>
					</select>
				</div>
			</div>

			{/* Filter panel — collapsible on mobile so it never blocks the first viewport */}
			<div className={cn("mt-4", mobileFiltersOpen ? "block" : "hidden lg:block")}>{filterPanel}</div>

			{/* Results */}
			{filtered.length === 0 ? (
				<div className="mt-10 rounded-xl border border-border bg-card py-12 text-center">
					<p className="text-lg text-muted-foreground">{t("noFilterResults")}</p>
					<button
						type="button"
						onClick={clearAll}
						className="hover:border-foreground/40 mt-4 inline-flex h-9 items-center rounded-lg border border-border px-3 text-sm font-medium"
					>
						{t("clearFilters")}
					</button>
				</div>
			) : (
				<div className="mt-8 grid gap-6 sm:grid-cols-2">
					{filtered.map((guide) => (
						<GuideCard
							key={guide.slug}
							title={guide.title}
							description={guide.description}
							slug={guide.slug}
							category={guide.categoryLabel}
							readingTime={guide.readingTime}
							readingTimeLabel={guide.readingTimeLabel}
							heroImage={guide.heroImage}
							heroImageAlt={guide.heroImageAlt}
							locale={locale}
							channel={channel}
						/>
					))}
				</div>
			)}
		</div>
	);
}
